import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faExternalLinkAlt,
  faCopy, // Add this import for duplicate icon
  faTimes, // Add this import for cancel
  faCheck, // Add this import for confirm
} from "@fortawesome/free-solid-svg-icons";
import {
  getSousEtablissements,
  deleteSousEtablissement,
  createSousEtablissement, // Add this import
} from "../../../services/api";
import { SousEtablissement } from "../../../types/types";
import { toast } from "react-toastify";
import { useLanguage } from "../../../context/LanguageContext";
import { getTranslation } from "../../../utils/translations";
import AdminLayout from "../AdminLayout";
import { resolveMediaUrl } from "../../../utils/media";

// Function to strip HTML tags
const stripHtmlTags = (html: string): string => {
  if (!html) return "";

  // Create a temporary div element
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Get text content and replace multiple spaces/newlines with single space
  const text = tempDiv.textContent || tempDiv.innerText || "";
  return text.replace(/\s+/g, " ").trim();
};

// Function to extract plain text from HTML (alternative method)
const extractPlainText = (html: string): string => {
  if (!html) return "";

  // Remove HTML tags
  let plainText = html.replace(/<[^>]*>/g, " ");

  // Replace HTML entities
  plainText = plainText
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'");

  // Clean up multiple spaces and newlines
  plainText = plainText.replace(/\s+/g, " ").trim();

  return plainText;
};

const SousEtablissementsList: React.FC = () => {
  const [sousEtablissements, setSousEtablissements] = useState<
    SousEtablissement[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null); // Track which item is being duplicated
  const { language } = useLanguage();
  const [duplicateLoading, setDuplicateLoading] = useState<number | null>(null); // Track loading state for duplicate

  useEffect(() => {
    fetchSousEtablissements();
  }, []);

  const fetchSousEtablissements = async () => {
    try {
      setLoading(true);
      const response = await getSousEtablissements();
      setSousEtablissements(response.data);
    } catch (error) {
      console.error("Error fetching sous-établissements:", error);
      toast.error("حدث خطأ أثناء جلب المؤسسات الفرعية");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("هل أنت متأكد من حذف هذه المؤسسة الفرعية؟")) {
      try {
        await deleteSousEtablissement(id);
        toast.success("تم حذف المؤسسة الفرعية بنجاح");
        fetchSousEtablissements();
      } catch (error) {
        console.error("Error deleting sous-établissement:", error);
        toast.error("حدث خطأ أثناء حذف المؤسسة الفرعية");
      }
    }
  };

  const handleDuplicate = (id: number) => {
    setDuplicatingId(id);
  };

  const confirmDuplicate = async (item: SousEtablissement) => {
    if (!item.id) return;

    setDuplicateLoading(item.id);

    try {
      // Prepare duplicate data
      const duplicateData = {
        titrearabe: `${item.titrearabe} (نسخة)`,
        titrefrancais: `${item.titrefrancais} (Copie)`,
        descarabe: item.descarabe || "",
        descfrancais: item.descfrancais || "",
        lien: item.lien || "",
        logo: item.logo, // Duplicate the logo as well
      };

      await createSousEtablissement(duplicateData);
      toast.success("تم نسخ المؤسسة الفرعية بنجاح");
      fetchSousEtablissements();
    } catch (error) {
      console.error("Error duplicating sous-établissement:", error);
      toast.error("حدث خطأ أثناء نسخ المؤسسة الفرعية");
    } finally {
      setDuplicateLoading(null);
      setDuplicatingId(null);
    }
  };

  const cancelDuplicate = () => {
    setDuplicatingId(null);
  };

  const filteredItems = sousEtablissements.filter(
    (item) =>
      item.titrearabe.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.titrefrancais.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.descarabe &&
        item.descarabe.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.descfrancais &&
        item.descfrancais.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  // Function to get plain text description
  const getPlainDescription = (
    html: string,
    maxLength: number = 50,
  ): string => {
    const plainText = extractPlainText(html);
    if (plainText.length > maxLength) {
      return plainText.substring(0, maxLength) + "...";
    }
    return plainText;
  };

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <h1 className="text-3xl font-bold text-mauritania-green-dark mb-4 md:mb-0">
          إدارة المؤسسات الفرعية
        </h1>
        <Link
          to="/admin/sousetablissements/new"
          className="bg-mauritania-green text-white py-2 px-4 rounded hover:bg-mauritania-green-dark transition duration-300"
        >
          <FontAwesomeIcon icon={faPlus} className="ml-2" />
          إضافة مؤسسة فرعية جديدة
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="relative">
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
          </div>
          <input
            type="text"
            className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:border-transparent"
            placeholder="ابحث في المؤسسات الفرعية..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-mauritania-green"></div>
          <p className="mt-2 text-gray-600">
            {getTranslation("loadingData", language)}
          </p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <p className="text-gray-600">لا توجد مؤسسات فرعية متاحة</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الشعار
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    العنوان (عربي)
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    العنوان (فرنسي)
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الرابط
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex-shrink-0 h-10 w-10">
                        {item.logo ? (
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={resolveMediaUrl(item.logo)}
                            alt={`شعار ${
                              item.titrearabe || item.titrefrancais
                            }`}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-400 text-xs">
                              لا يوجد شعار
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {item.titrearabe}
                      </div>
                      <div
                        className="text-sm text-gray-500 truncate max-w-xs"
                        title={extractPlainText(item.descarabe || "")}
                      >
                        {getPlainDescription(item.descarabe || "")}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {item.titrefrancais}
                      </div>
                      <div
                        className="text-sm text-gray-500 truncate max-w-xs"
                        title={extractPlainText(item.descfrancais || "")}
                      >
                        {getPlainDescription(item.descfrancais || "")}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.lien ? (
                        <a
                          href={item.lien}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-900 text-sm flex items-center"
                        >
                          <FontAwesomeIcon
                            icon={faExternalLinkAlt}
                            className="ml-1"
                          />
                          زيارة الموقع
                        </a>
                      ) : (
                        <span className="text-gray-400 text-sm">
                          لا يوجد رابط
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {duplicatingId === item.id ? (
                        <div className="flex space-x-2 space-x-reverse">
                          <button
                            onClick={() => confirmDuplicate(item)}
                            disabled={duplicateLoading === item.id}
                            className="text-green-600 hover:text-green-900 disabled:opacity-50"
                            title="تأكيد النسخ"
                          >
                            {duplicateLoading === item.id ? (
                              <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-green-600"></div>
                            ) : (
                              <FontAwesomeIcon icon={faCheck} />
                            )}
                          </button>
                          <button
                            onClick={cancelDuplicate}
                            className="text-red-600 hover:text-red-900"
                            title="إلغاء"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex space-x-3 space-x-reverse">
                          <Link
                            to={`/admin/sousetablissements/edit/${item.id}`}
                            className="text-mauritania-green hover:text-mauritania-green-dark"
                            title="تعديل"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Link>
                          <button
                            onClick={() => handleDuplicate(item.id!)}
                            className="text-blue-600 hover:text-blue-900"
                            title="نسخ"
                          >
                            <FontAwesomeIcon icon={faCopy} />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id!)}
                            className="text-red-600 hover:text-red-900"
                            title="حذف"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Show duplicate confirmation info when an item is being duplicated */}
          {duplicatingId && (
            <div className="bg-yellow-50 border-r-4 border-yellow-400 p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FontAwesomeIcon icon={faCopy} className="text-yellow-400" />
                </div>
                <div className="mr-3 flex-1">
                  <p className="text-sm text-yellow-700">
                    قم بتأكيد نسخ المؤسسة الحالية. سيتم إنشاء نسخة جديدة بنفس
                    البيانات مع إضافة "(نسخة)" للعناوين.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default SousEtablissementsList;
